import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import styled, { css } from "@rentiohq/web-theme";
import { RM_COLORS } from "../rentmatch.colors";

export type TNoShowLabelVariant = "inline" | "attached";

const attached = css`
  height: 32px;
  padding: 0 12px;
  gap: 6px;
  border: 1px solid ${RM_COLORS.gray300};
  /* Left half of a pair: the chevron beside it rounds the right half and pulls
     itself one pixel over this border, the same way the candidate list joins
     its action buttons. */
  border-radius: 6px 0 0 6px;
  font-size: 13px;
  font-weight: 500;
`;

const inline = css`
  height: 18px;
  padding: 0 4px 0 3px;
  gap: 3px;
  border: 1px solid ${RM_COLORS.gray300};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
`;

const Label = styled.span<{ $variant: TNoShowLabelVariant }>`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  background: ${RM_COLORS.redBg};
  color: ${RM_COLORS.redDark};
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    flex-shrink: 0;
  }

  ${({ $variant }) => ($variant === "attached" ? attached : inline)}
`;

/**
 * A no-show on the property being looked at, said plainly instead of hidden in
 * the warning triangle beside it.
 *
 * The triangle is for what happened elsewhere: no-shows on the candidate's
 * other properties, and the notes colleagues left there. Repeating the no-show
 * of the property already on screen only made it harder to see which of the
 * two the triangle was warning about.
 *
 * Read-only on purpose. The one place a no-show is switched on and off is the
 * action button in the candidate list, which turns the same red while it is on,
 * so the two say the same thing in the same color.
 *
 * Pale red rather than filled red, the same pair the Rentmatch score uses for a
 * low score, and the same gray border every action button has. Filled red
 * shouted across the row.
 */
/**
 * The thumb the candidate list draws on its negative actions, the no-show
 * button among them, at the same 14px and copied rather than shared because
 * that list keeps its icons inline too.
 */
const ThumbsDown = (props: { size: number }) => (
  <svg
    width={props.size}
    height={props.size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
    <path d="M17 2h4a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4" />
  </svg>
);

export const NoShowLabel = (props: {
  noShowAt: Date | string;
  /**
   * "attached" sits against the chevron at the end of a row, where the
   * candidate list puts its own no-show button. "inline" is the smaller pill
   * for next to a name.
   */
  variant?: TNoShowLabelVariant;
}) => {
  const { noShowAt, variant = "inline" } = props;
  const date = noShowAt instanceof Date ? noShowAt : new Date(noShowAt);

  return (
    <Label
      $variant={variant}
      title={getLocalizedText("application.no_show.on_date", {
        date: formatDate(date, "d MMMM yyyy"),
      })}
    >
      <ThumbsDown size={variant === "attached" ? 14 : 11} />
      {getLocalizedText("application.action.no_show")}
    </Label>
  );
};
